import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import ReactResizeDetector from 'react-resize-detector';
import styled, { css, useTheme } from 'styled-components';

import * as PopperJS from '@popperjs/core';

import Button from './base/Button';
import { StyledCard } from './base/Card';
import { IconName } from './base/Icon';
import { Div, StyledUtilsProps } from './helpers/StyledUtils';
import Text from './base/Text';
import { DROPDOWN_WIDTHS } from '@hookdeck/theme';

export interface DropdownProps extends StyledUtilsProps {
  active?: boolean;
  disabled?: boolean;
  placement?: PopperJS.Placement;
  label_prefix?: string;
  label?: React.ReactNode;
  block?: boolean;
  warp_with_card?: boolean;
  prevent_event_on_children?: boolean;
  scroll_to_selected?: boolean;
  portal?: boolean;
  on_hover?: boolean;
  small?: boolean;
  large?: boolean;
  offset?: [number, number];
  /**
   * Custom width for the dropdown. Use `parent_size` instead if possible.
   */
  parent_width?:
    | boolean
    | {
        min?: number;
        max?: number;
      };
  /**
   * Fixed width for the dropdown content without changing the toggle width.
   */
  dropdown_width?: number;
  /**
   * Design system width for the dropdown. Use this instead of `parent_width` if possible.
   */
  parent_size?: keyof typeof DROPDOWN_WIDTHS;
  children:
    | React.ReactNode
    | ((toggle: (show: boolean) => void, update?: () => void) => React.ReactNode);

  // Button props
  primary?: boolean;
  outline?: boolean;
  danger?: boolean;
  ghost?: boolean;
  icon?: IconName;
  expand_icon?: IconName;
  badge?: {
    label: string;
  };

  fit_to_screen?: boolean;

  z_index?: number;

  onFocus?: () => void;
  onToggle?(opened: boolean): void;
  renderToggle?(
    opened: boolean,
    toggle: (opened: boolean, e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
  ): React.JSX.Element;
  onBeforeClose?: () => Promise<boolean> | boolean;
}

export const StyledDropdownWrapper = styled.div<{ block?: boolean }>`
  height: max-content;

  ${({ block }) =>
    block &&
    css`
      width: 100%;
    `}
`;

const StyledDropdownCardWrapper = styled.div<{
  width?: string;
  min_width?: string;
  max_width?: string;
  z_index?: number;
}>`
  z-index: ${({ theme, z_index }) => (z_index ? z_index : theme.zindex.dropdown)};

  ${({ min_width }) => css`
    min-width: ${min_width};
  `}

  ${({ max_width }) => css`
    max-width: ${max_width};
  `}

    ${({ max_width, min_width }) =>
    max_width &&
    min_width &&
    css`
      width: 100%;
    `}
`;

const StyledBackground = styled.div<{ z_index?: number; pointer_events?: boolean }>`
  z-index: ${({ theme, z_index }) => (z_index ? z_index - 1 : theme.zindex.dropdown - 1)};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: ${({ pointer_events = true }) => (pointer_events ? 'auto' : 'none')};
`;

export const StyledDropdown = styled(Div)<DropdownProps & { card?: boolean }>`
  > div {
    ${({ card }) =>
      !card &&
      css`
        box-shadow: ${({ theme }) => theme.elevation[2]};
      `}
    width: 100%;
  }
`;

const Dropdown: React.FC<DropdownProps> = (props) => {
  const {
    active,
    label,
    label_prefix,
    icon,
    expand_icon,
    prevent_event_on_children,
    parent_width,
    dropdown_width,
    parent_size,
    onFocus,
    onToggle,
    offset,
    renderToggle,
    children,
    portal = false,
    on_hover = false,
    warp_with_card = true,
    fit_to_screen = true,
    scroll_to_selected = false,
  } = props;
  const [opened, toggle] = useState(active ? true : false);
  const [referenceElement, setReferenceElement] = useState<Element | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  // react-resize-detector v8's class component falls back to the removed
  // ReactDOM.findDOMNode under React 19. Passing `targetRef` makes it observe this
  // node directly; we merge it with the popper reference ref on the toggle element.
  const resize_target_ref = useRef<HTMLElement | null>(null);
  const setToggleRef = (el: HTMLElement | null) => {
    resize_target_ref.current = el;
    setReferenceElement(el);
  };
  const [pointer_events, setPointerEvents] = useState(true);
  const hover_open_timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const theme = useTheme();
  useEffect(() => {
    if (opened && onFocus !== undefined) {
      onFocus();
    }

    if (opened && scroll_to_selected && popperElement) {
      setPointerEvents(false);
      // Use a small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        const selectedElement = popperElement.querySelector('[data-selected="true"]');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' });
        }
      }, 10);

      setTimeout(() => {
        setPointerEvents(true);
      }, 100);
    }
  }, [opened, popperElement, scroll_to_selected]);

  const placement: PopperJS.Placement = props.placement || 'bottom-start';

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          mainAxis: true,
          altAxis: true,
          padding: 8,
        },
      },
      {
        name: 'offset',
        options: {
          offset: offset || [0, 4],
        },
      },
    ],
  });

  const handleToggle = async (opened: boolean) => {
    if (active) {
      return;
    }
    // Prevent closing if onBeforeClose returns false
    if (!opened && props.onBeforeClose) {
      let canClose = true; // Default to true if onBeforeClose fails
      try {
        canClose = await props.onBeforeClose();
      } catch (error) {
        console.error('Error during Dropdown onBeforeClose:', error);
        // Allow closing if the check fails unexpectedly
      }
      if (!canClose) {
        return;
      }
    }
    if (onToggle) {
      onToggle(opened);
    }
    toggle(opened);
  };

  useEffect(() => {
    if (!opened || !on_hover) {
      return;
    }

    const isPointerInHoverBridge = (event: PointerEvent) => {
      if (!referenceElement || !popperElement) {
        return true;
      }

      const reference_rect = referenceElement.getBoundingClientRect();
      const popper_rect = popperElement.getBoundingClientRect();
      const hover_tolerance = 8;
      const pointer_x = event.clientX;
      const pointer_y = event.clientY;
      const inVerticalOverlap =
        pointer_y >= Math.max(reference_rect.top, popper_rect.top) - hover_tolerance &&
        pointer_y <= Math.min(reference_rect.bottom, popper_rect.bottom) + hover_tolerance;
      const inHorizontalOverlap =
        pointer_x >= Math.max(reference_rect.left, popper_rect.left) - hover_tolerance &&
        pointer_x <= Math.min(reference_rect.right, popper_rect.right) + hover_tolerance;

      if (popper_rect.left >= reference_rect.right) {
        return (
          pointer_x >= reference_rect.right - hover_tolerance &&
          pointer_x <= popper_rect.left + hover_tolerance &&
          inVerticalOverlap
        );
      }

      if (reference_rect.left >= popper_rect.right) {
        return (
          pointer_x >= popper_rect.right - hover_tolerance &&
          pointer_x <= reference_rect.left + hover_tolerance &&
          inVerticalOverlap
        );
      }

      if (popper_rect.top >= reference_rect.bottom) {
        return (
          pointer_y >= reference_rect.bottom - hover_tolerance &&
          pointer_y <= popper_rect.top + hover_tolerance &&
          inHorizontalOverlap
        );
      }

      if (reference_rect.top >= popper_rect.bottom) {
        return (
          pointer_y >= popper_rect.bottom - hover_tolerance &&
          pointer_y <= reference_rect.top + hover_tolerance &&
          inHorizontalOverlap
        );
      }

      return false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const target = event.target;
      const inside_dropdown =
        target instanceof Node &&
        (referenceElement?.contains(target) || popperElement?.contains(target));

      if (inside_dropdown || isPointerInHoverBridge(event)) {
        return;
      }

      if (hover_open_timeout.current) {
        clearTimeout(hover_open_timeout.current);
        hover_open_timeout.current = undefined;
      }
      handleToggle(false);
    };

    document.addEventListener('pointermove', handlePointerMove);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      if (hover_open_timeout.current) {
        clearTimeout(hover_open_timeout.current);
        hover_open_timeout.current = undefined;
      }
    };
  }, [opened, on_hover, referenceElement, popperElement]);

  const handleToggleWithPreventDefault = (
    opened: boolean,
    e?: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (e && prevent_event_on_children) {
      e.preventDefault();
      if (e.target === e.currentTarget) handleToggle(opened);
      return;
    }
    handleToggle(opened);
  };

  const handleUpdate = () => {
    if (update !== null) {
      update();
    }
  };

  const create_portal = (children: React.ReactNode, element: HTMLElement, portal: boolean) => {
    if (portal) {
      return createPortal(children, element);
    }
    return children;
  };

  useEffect(() => {
    handleUpdate();
  }, [children]);

  const handleHoverOpen = () => {
    if (opened || hover_open_timeout.current) {
      return;
    }

    hover_open_timeout.current = setTimeout(() => {
      hover_open_timeout.current = undefined;
      handleToggle(true);
    }, 100);
  };

  const handleHoverLeave = () => {
    if (hover_open_timeout.current) {
      clearTimeout(hover_open_timeout.current);
      hover_open_timeout.current = undefined;
    }
  };

  const reference_hover_props = on_hover
    ? {
        onMouseEnter: handleHoverOpen,
        onMouseLeave: handleHoverLeave,
        onPointerEnter: handleHoverOpen,
        onPointerLeave: handleHoverLeave,
      }
    : {};

  // Calculate min/max width values outside JSX for better type inference
  let min_width_value: string | undefined;
  let max_width_value: string | undefined;
  if (dropdown_width) {
    min_width_value = `${dropdown_width}px`;
    max_width_value = `${dropdown_width}px`;
  } else if (parent_width) {
    if (typeof parent_width === 'object') {
      min_width_value = parent_width.min
        ? `${parent_width.min}px`
        : `${referenceElement?.clientWidth}px`;
      max_width_value = parent_width.max
        ? `${parent_width.max}px`
        : `${referenceElement?.clientWidth}px`;
    } else if (parent_size) {
      min_width_value = `${DROPDOWN_WIDTHS[parent_size]}px`;
      max_width_value = `${DROPDOWN_WIDTHS[parent_size]}px`;
    } else {
      // parent_width is true
      min_width_value = `${referenceElement?.clientWidth}px`;
      max_width_value = `${referenceElement?.clientWidth}px`;
    }
  }

  return (
    <StyledDropdownWrapper block={!!props.block}>
      <ReactResizeDetector
        handleHeight
        onResize={handleUpdate}
        targetRef={resize_target_ref as React.RefObject<HTMLElement>}>
        {renderToggle ? (
          <div ref={setToggleRef} {...reference_hover_props}>
            {renderToggle(opened, handleToggleWithPreventDefault)}
          </div>
        ) : (
          <div ref={setToggleRef} {...reference_hover_props}>
            <Button
              small={props.small}
              large={props.large}
              m={props.m}
              outline={props.outline}
              primary={props.primary}
              danger={props.danger}
              ghost={props.ghost}
              badge={props.badge}
              active={opened}
              dropdown
              block={!!props.block}
              disabled={props.disabled}
              icon={icon ? icon : undefined}
              expand_icon={expand_icon}
              onClick={() => handleToggle(!opened)}>
              {label || label_prefix ? (
                <>
                  {label_prefix && (
                    <Text semi muted m={{ r: 1 }}>
                      {label_prefix}
                    </Text>
                  )}
                  {label}
                </>
              ) : undefined}
            </Button>
          </div>
        )}
      </ReactResizeDetector>
      {opened &&
        create_portal(
          <React.Fragment>
            <StyledDropdownCardWrapper
              z_index={props.z_index}
              min_width={min_width_value}
              max_width={max_width_value}
              ref={fit_to_screen ? setPopperElement : undefined}
              style={
                fit_to_screen
                  ? styles.popper
                  : { marginTop: '4px', zIndex: theme.zindex.dropdown, position: 'relative' }
              }
              {...(fit_to_screen ? attributes.popper : {})}>
              <StyledDropdown card={warp_with_card}>
                {warp_with_card ? (
                  <StyledCard
                    {...(props as Omit<DropdownProps, 'onToggle'>)}
                    style={{ maxHeight: '90vh', overflowY: 'auto' }}
                    raised={3}
                    overflow_hidden>
                    {typeof children === 'function'
                      ? children(handleToggleWithPreventDefault, update || undefined)
                      : children}
                  </StyledCard>
                ) : typeof children === 'function' ? (
                  children(handleToggleWithPreventDefault, update || undefined)
                ) : (
                  children
                )}
              </StyledDropdown>
            </StyledDropdownCardWrapper>
            {!on_hover && (
              <StyledBackground
                z_index={props.z_index}
                pointer_events={pointer_events}
                onClick={() => handleToggleWithPreventDefault(false)}
              />
            )}
          </React.Fragment>,
          document.body,
          portal,
        )}
    </StyledDropdownWrapper>
  );
};

export default Dropdown;
